import React from 'react';
import { Search, X } from 'lucide-react';

interface Filters {
  completionStatus: string;
  stepType: string;
  searchTerm: string;
}

interface ProcessStepMapping {
  processStep: {
    id: number;
    processStepId: string;
    stepNumber: number;
    processStepName: string;
    description: string;
    processStepType: string;
    frequency: string;
    volume: string;
    performingCountry: string;
    legalEntity: string;
    supportingModel: string;
    thirdPartyBusinessType: string;
  };
  riskControlMappings: any[];
  summary: {
    totalRisks: number;
    totalControls: number;
    totalMappings: number;
    completionStatus: string;
    needsAttention: boolean;
  };
}

interface MappingFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  mappingData: ProcessStepMapping[];
}

export const MappingFilters: React.FC<MappingFiltersProps> = ({
  filters,
  onFiltersChange,
  mappingData
}) => {
  // Extract unique values for filter options
  const completionStatuses = Array.from(
    new Set(mappingData.map(item => item.summary.completionStatus))
  ).filter(Boolean);

  const stepTypes = Array.from(
    new Set(mappingData.map(item => item.processStep.processStepType))
  ).filter(Boolean);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      completionStatus: 'all',
      stepType: 'all',
      searchTerm: ''
    });
  };

  const hasActiveFilters = 
    filters.completionStatus !== 'all' || 
    filters.stepType !== 'all' || 
    filters.searchTerm !== '';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#221551]">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Process Steps
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="Search by name..."
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#57C8F4] focus:border-transparent"
            />
          </div>
        </div>

        {/* Completion Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Completion Status
          </label>
          <select
            value={filters.completionStatus}
            onChange={(e) => handleFilterChange('completionStatus', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#57C8F4] focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            {completionStatuses.map(status => (
              <option key={status} value={status}>
                {status.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Step Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Process Step Type
          </label>
          <select
            value={filters.stepType}
            onChange={(e) => handleFilterChange('stepType', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#57C8F4] focus:border-transparent"
          >
            <option value="all">All Types</option>
            {stepTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Quick Filters */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quick Filters
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleFilterChange('completionStatus', 'INCOMPLETE')}
              className="px-3 py-1 text-xs bg-orange-100 text-orange-800 rounded-full hover:bg-orange-200 transition-colors"
            >
              Needs Attention
            </button>
            <button
              onClick={() => handleFilterChange('completionStatus', 'COMPLETE')}
              className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full hover:bg-green-200 transition-colors"
            >
              Complete
            </button>
            <button
              onClick={() => handleFilterChange('completionStatus', 'NEEDS_RISKS')}
              className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full hover:bg-yellow-200 transition-colors"
            >
              Needs Risks
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Active filters:</span>
            
            {filters.searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                Search: "{filters.searchTerm}"
                <button 
                  onClick={() => handleFilterChange('searchTerm', '')}
                  className="hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {filters.completionStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                Status: {filters.completionStatus.replace('_', ' ')}
                <button 
                  onClick={() => handleFilterChange('completionStatus', 'all')}
                  className="hover:text-purple-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {filters.stepType !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full">
                Type: {filters.stepType}
                <button 
                  onClick={() => handleFilterChange('stepType', 'all')}
                  className="hover:text-green-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};